import React, { useEffect, useMemo, useRef, useState } from "react";
import { Season } from "@mysnowscape/snow-lib";

import type { RouteType } from "../../../../../../types/routeBuilder";
import ChevronDownIcon from "../../../../../../resources/route-builder/chevron-down.svg";
import { formatAddress } from "../../../RoutePage/Routes/utils";
import { useRouteBuilder } from "../../context/RouteBuilderContext";
import { defaultTemporaryExpiresAt } from "../../lib/defaultInputs";
import { propertyMatchesSearch } from "../../lib/helpers";
import { RECENCY_OPTIONS, shouldShowRecency } from "../../lib/serviceHelpers";
import RouteSelectionCheckbox from "../RouteSelectionCheckbox";

const SEASONS = [Season.snow, Season.landscape] as const;

const ROUTE_TYPE_LABELS: Record<RouteType, string> = {
  recurring: "Recurring",
  temporary: "One-time",
};

function toggleInList<T>(list: T[] | undefined, item: T): T[] {
  const current = list ?? [];
  return current.includes(item)
    ? current.filter((value) => value !== item)
    : [...current, item];
}

export default function PropertyFiltersStep() {
  const {
    inputs,
    setInputs,
    availableServices,
    isRefreshingProperties,
    selectedPropertyCount,
    allProperties,
  } = useRouteBuilder();
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [propertyQuery, setPropertyQuery] = useState("");
  const propertiesSectionRef = useRef<HTMLElement>(null);
  const isTemporary = inputs.routeType === "temporary";
  const showRecency = shouldShowRecency(inputs.routeType, inputs.season);
  const visibleProperties = useMemo(
    () =>
      allProperties.filter((property) =>
        propertyMatchesSearch(property, propertyQuery),
      ),
    [allProperties, propertyQuery],
  );

  useEffect(() => {
    if (propertiesOpen) {
      propertiesSectionRef.current?.scrollIntoView?.({
        block: "start",
        behavior: "smooth",
      });
    }
  }, [propertiesOpen]);

  const toggleProperty = (propertyId: string) => {
    setInputs((prev) => ({
      ...prev,
      excludedPropertyIds: toggleInList(prev.excludedPropertyIds, propertyId),
    }));
  };

  return (
    <>
      <fieldset>
        <legend className="text-sm font-semibold text-slate-700">
          Route type
        </legend>
        <div className="mt-2 flex gap-2">
          {(["recurring", "temporary"] as const).map((routeType) => (
            <button
              key={routeType}
              type="button"
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                inputs.routeType === routeType
                  ? "bg-snow-primary text-white"
                  : "border border-slate-300 text-slate-700 hover:bg-slate-50"
              }`}
              onClick={() =>
                setInputs((prev) => ({
                  ...prev,
                  routeType,
                  expiresAt:
                    routeType === "temporary"
                      ? prev.expiresAt ||
                        defaultTemporaryExpiresAt(prev.plannedDispatchTime)
                      : undefined,
                  propertyFilters: {
                    ...prev.propertyFilters,
                    recencyOfService:
                      routeType === prev.routeType
                        ? prev.propertyFilters.recencyOfService
                        : null,
                  },
                }))
              }
            >
              {ROUTE_TYPE_LABELS[routeType]}
            </button>
          ))}
        </div>
        {isTemporary ? (
          <p className="mt-2 text-xs text-amber-700">
            One-time routes are removed once they expire.
          </p>
        ) : null}
      </fieldset>

      <fieldset>
        <legend className="text-sm font-semibold text-slate-700">Season</legend>
        <div className="mt-2 flex gap-2">
          {SEASONS.map((season) => (
            <button
              key={season}
              type="button"
              className={`rounded-lg px-4 py-2 text-sm font-medium capitalize ${
                inputs.season === season
                  ? "bg-snow-primary text-white"
                  : "border border-slate-300 text-slate-700 hover:bg-slate-50"
              }`}
              onClick={() =>
                setInputs((prev) => ({
                  ...prev,
                  season,
                  propertyFilters: {
                    ...prev.propertyFilters,
                    serviceTypes: [],
                    recencyOfService: shouldShowRecency(prev.routeType, season)
                      ? prev.propertyFilters.recencyOfService
                      : null,
                  },
                }))
              }
            >
              {season}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset data-testid="services-fieldset">
        <legend className="text-sm font-semibold text-slate-700">
          Services
        </legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {availableServices.length === 0 ? (
            <p className="text-sm text-slate-500">
              No services configured for this company and season.
            </p>
          ) : (
            availableServices.map((service) => {
              const selected =
                inputs.propertyFilters.serviceTypes?.includes(service.key) ??
                false;
              return (
                <button
                  key={service.key}
                  type="button"
                  data-testid={`service-chip-${service.key}`}
                  className={`rounded-full px-3 py-1 text-xs font-medium capitalize ${
                    selected
                      ? "bg-sky-100 text-sky-800 ring-1 ring-sky-300"
                      : "bg-slate-100 text-slate-600"
                  }`}
                  onClick={() =>
                    setInputs((prev) => ({
                      ...prev,
                      propertyFilters: {
                        ...prev.propertyFilters,
                        serviceTypes: toggleInList(
                          prev.propertyFilters.serviceTypes,
                          service.key,
                        ),
                      },
                    }))
                  }
                >
                  {service.label}
                </button>
              );
            })
          )}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Leave empty to include all services for this season.
        </p>
      </fieldset>

      {showRecency ? (
        <fieldset
          className="rounded-xl bg-amber-50 p-3 ring-1 ring-amber-200"
          data-testid="recency-fieldset"
        >
          <legend className="text-sm font-semibold text-slate-700">
            Recency of service
            {isTemporary ? " (recommended for ad-hoc snow)" : ""}
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {RECENCY_OPTIONS[inputs.routeType].map((option) => (
              <button
                key={String(option.value)}
                type="button"
                className={`rounded-lg px-3 py-1.5 text-sm ${
                  inputs.propertyFilters.recencyOfService === option.value
                    ? "bg-snow-primary text-white"
                    : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                }`}
                onClick={() =>
                  setInputs((prev) => ({
                    ...prev,
                    propertyFilters: {
                      ...prev.propertyFilters,
                      recencyOfService: option.value,
                    },
                  }))
                }
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>
      ) : (
        <p
          className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500"
          data-testid="recency-hidden-note"
        >
          Recency of service applies to recurring routes and one-time snow
          routes.
        </p>
      )}

      <section
        ref={propertiesSectionRef}
        className="scroll-mt-4 rounded-xl border border-slate-200 bg-slate-50/80"
      >
        <button
          type="button"
          data-testid="matching-properties-toggle"
          aria-expanded={propertiesOpen}
          aria-controls="matching-properties-list"
          className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
          onClick={() => setPropertiesOpen((current) => !current)}
        >
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-700">
              Properties matching filters
            </span>
            <span
              className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
              data-testid="filtered-property-count"
            >
              {selectedPropertyCount}
            </span>
            {isRefreshingProperties ? (
              <span className="text-xs font-medium text-sky-700">
                Loading...
              </span>
            ) : null}
          </span>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700">
            {propertiesOpen ? "Collapse" : "Expand"}
            <img
              src={ChevronDownIcon}
              alt=""
              className={`h-3 w-3 transition-transform ${
                propertiesOpen ? "rotate-180" : ""
              }`}
              aria-hidden
            />
          </span>
        </button>

        {propertiesOpen ? (
          <div className="border-t border-slate-200 px-1.5 pb-1.5 pt-1">
            {allProperties.length > 5 || propertyQuery ? (
              <input
                data-testid="matching-properties-search"
                type="search"
                value={propertyQuery}
                aria-label="Search matching properties"
                placeholder="Search properties"
                className="mb-1.5 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-snow-blue-3"
                onChange={(event) => setPropertyQuery(event.target.value)}
              />
            ) : null}
            <ul
              id="matching-properties-list"
              className="list-none"
              data-testid="matching-properties-list"
            >
              {visibleProperties.length === 0 ? (
                <li className="px-2 py-2 text-sm text-slate-500">
                  {allProperties.length === 0
                    ? "No properties match the current filters."
                    : `No properties match “${propertyQuery.trim()}”`}
                </li>
              ) : (
                visibleProperties.map((property) => {
                  const address = formatAddress(property.address).trim();
                  const checked = !inputs.excludedPropertyIds?.includes(
                    property.jobId,
                  );
                  return (
                    <li
                      key={property.jobId}
                      className="flex items-start gap-3 rounded-lg px-2 py-2 hover:bg-white"
                    >
                      <RouteSelectionCheckbox
                        state={checked ? "checked" : "unchecked"}
                        label={`Include ${property.name}`}
                        testId={`matching-property-${property.jobId}`}
                        disabled={isRefreshingProperties}
                        className="mt-0.5"
                        onChange={() => toggleProperty(property.jobId)}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-700">
                          {property.name}
                        </p>
                        {address ? (
                          <p className="truncate text-xs text-slate-500">
                            {address}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        ) : null}
      </section>
    </>
  );
}
